import { useEffect, useRef } from "react";
import { setTurnstileToken } from "../lib/turnstileBridge";

interface TurnstileRenderOptions {
  sitekey: string;
  appearance?: "always" | "execute" | "interaction-only";
  retry?: "auto" | "never";
  "refresh-expired"?: "auto" | "manual" | "never";
  callback?: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
      remove: (widgetId: string) => void;
    };
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SCRIPT_ID = "cf-turnstile-script";

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) {
    return new Promise((resolve) => {
      existing.addEventListener("load", () => resolve(), { once: true });
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Cloudflare Turnstile"));
    document.head.appendChild(script);
  });
}

/**
 * Renders a Cloudflare Turnstile challenge and keeps `turnstileBridge`'s token fresh via
 * auto-refresh, so a valid token is very likely ready by the time the user finishes the email
 * OTP flow. Mount this unconditionally on SignInPage (not gated behind a step) so it has the
 * whole sign-in flow's duration to solve/refresh in the background.
 *
 * `appearance: "interaction-only"` means most real users never see any UI at all — the widget
 * only renders visibly if Cloudflare decides this particular visitor needs an interactive
 * challenge. This is deliberately scoped to the sign-in page rather than a site-wide/page-load
 * check — see SECURITY.md's "Signup abuse prevention" section for why.
 */
export function TurnstileWidget({ siteKey }: { siteKey: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          appearance: "interaction-only",
          retry: "auto",
          "refresh-expired": "auto",
          callback: (token) => setTurnstileToken(token),
          "expired-callback": () => setTurnstileToken(null),
          "error-callback": () => setTurnstileToken(null),
        });
      })
      .catch(() => {
        // Script failed to load (network/ad-blocker/etc). Leave the token null — the backend
        // will reject new-account creation with HUMAN_VERIFICATION_REQUIRED, which the sign-in
        // flow surfaces as a normal retryable error, same as any other OTP failure.
        setTurnstileToken(null);
      });

    return () => {
      cancelled = true;
      setTurnstileToken(null);
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
  }, [siteKey]);

  return <div ref={containerRef} />;
}
